import React, { useEffect, useState, useRef} from 'react';
import {push} from "connected-react-router";
import {useSelector, useDispatch} from 'react-redux';

import { loadModules } from 'esri-loader';
import { esriOptions } from '../../../../config/config'

import useDataSource from "../../../../hooks/useDataSource";


import { styled, LinearProgress } from '@mui/material';


const PREFIX = 'SmartMap';

const classes = {
    root: `${PREFIX}-root`,
    mapDiv: `${PREFIX}-mapDiv`,
    mapMask: `${PREFIX}-mapMask`,
    mapMaskHidden: `${PREFIX}-mapMaskHidden`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        flexGrow: 1,
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        fontFamily: 'Roboto',
    },

    [`& .${classes.mapDiv}`]: {
        padding: 0,
        margin: 0,
        height: '100%',
        width: '100%',
        overflowY: 'hidden',
        position: 'absolute',
        top: '0px',
    },

    [`& .${classes.mapMask}`]: {
        padding: 0,
        margin: 0,
        height: '100%',
        width: '100%',
        overflowY: 'hidden',
        position: 'absolute',
        top: '0px',
        // backgroundColor: '#f1f1f1'
        // backgroundColor: '#FFF',
    },

    [`& .${classes.mapMaskHidden}`]: {
        display: 'none',
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

export default React.memo(function Map({configuration}) {

    const elementRef = useRef();
    // const theme = useTheme();

    // TODO: Pull from config, render types, categories, advanced functions with existing GIS data
    const pointStyleName = 'push-pin-2';

    const esriIdentity = configuration.element?.esriIdentity ? configuration.element.esriIdentity : {};
    const useThemeBasemap = configuration.element?.useThemeBasemap !== undefined ? configuration.element.useThemeBasemap : false;
    const zoomVisible = configuration.element?.zoomVisible !== undefined ? configuration.element.zoomVisible : false;
    const homeVisible = configuration.element?.homeVisible !== undefined ? configuration.element.homeVisible : false;
    const compassVisible = configuration.element?.compassVisible !== undefined ? configuration.element.compassVisible : false;
    const basemapGalleryVisible = configuration.element?.basemapGalleryVisible !== undefined ? configuration.element.basemapGalleryVisible : false;
    const legendVisible = configuration.element?.legendVisible !== undefined ? configuration.element.legendVisible : false;
    const layersVisible = configuration.element?.layersVisible !== undefined ? configuration.element.layersVisible : false;
    const disableGestures = configuration.element?.disableGestures !== undefined ? configuration.element.disableGestures : true;
    const zoomLevel = configuration.element?.zoomLevel ? configuration.element.zoomLevel : 9;
    const panAndZoom = configuration.element?.panAndZoom !== undefined ? configuration.element.panAndZoom : false;
    const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';

    // App State
    const dataSource = useDataSource(dataSourceName);
    const config = useSelector(state => state.config);
    const isThemeLight = useSelector(state => state.persist.themeLight);

    // Component State
    // const [signedIn, setSignedIn] = useState(false);
    const [webMap, setWebMap] = useState(null);
    const [mapView, setMapView] = useState(null);
    const [projectFeatureLayer, setProjectFeatureLayer] = useState(null);
    const [graphics, setGraphics] = useState([]);
    const [mapVisible, setMapVisible] = useState(false);
    const [mapLoading, setMapLoading] = useState(true);
    const [showProgress, setShowProgress] = useState(configuration.element?.showFetching !== undefined ? configuration.element?.showFetching : true);

    const dispatch = useDispatch();

    // Load WebMap - pass [] so there is no watch
    useEffect(() => {
        loadModules(
            [
                'esri/identity/OAuthInfo',
                'esri/identity/IdentityManager',
                'esri/config',
                'esri/core/urlUtils',
                'esri/core/watchUtils',
                'esri/WebMap',
                'esri/views/MapView',
                "esri/layers/FeatureLayer",
                'esri/widgets/Compass',
                'esri/widgets/Home',
                'esri/widgets/Zoom',
                'esri/widgets/BasemapGallery',
                'esri/widgets/Expand',
                'esri/widgets/Legend',
                'esri/widgets/LayerList'
            ],
            JSON.parse(esriOptions())
        ).then(
            ([
                 OAuthInfo,
                 esriId,
                 esriConfig,
                 urlUtils,
                 watchUtils,
                 WebMap,
                 MapView,
                 FeatureLayer,
                 Compass,
                 Home,
                 Zoom,
                 BasemapGallery,
                 Expand,
                 Legend,
                 LayerList
             ]) => {

                // Setup source
                esriConfig.portalUrl = esriIdentity.portalUrl;

                // WebMap from AGSOL
                const webMap = new WebMap({
                    portalItem: {
                        id: esriIdentity.portalItem.id
                    },
                });

                // NOTE: Currently using WebMap Extent
                // const zoom = 10;
                // const centerPoint = new Point({
                //     x: '-112.443692',
                //     y: '33.450515',
                // });

                // MapView
                const mapComponents = [];
                if (zoomVisible)
                    mapComponents.push("zoom");

                const view = new MapView({
                    container: elementRef.current, // "viewDiv",
                    map: webMap,
                    //zoom: zoom,
                    //center: centerPoint,
                    // exlude the zoom widget from the default UI
                    ui: {
                        components: mapComponents
                    }
                });

                // *************************************************
                // HOME
                // *************************************************
                if (homeVisible) {
                    const homeWidget = new Home({
                        view: view,
                    });

                    // adds the home widget to the top left corner of the MapView
                    view.ui.add(homeWidget, 'top-left');
                }

                // *************************************************
                // Compass
                // *************************************************
                if (compassVisible) {
                    var compassWidget = new Compass({
                        view: view,
                    });

                    // Add the Compass widget to the top left corner of the view
                    view.ui.add(compassWidget, 'top-left');
                }

                // *************************************************
                // BASEMAP GALLERY
                // *************************************************
                if (basemapGalleryVisible) {
                    // Create a BasemapGallery widget instance and set
                    // its container to a div element
                    let basemapGallery = new BasemapGallery({
                        view: view,
                        container: document.createElement('div'),
                    });

                    // Create an Expand instance and set the content
                    // property to the DOM node of the basemap gallery widget
                    // Use an Esri icon font to represent the content inside
                    // of the Expand widget
                    const bgExpand = new Expand({
                        view: view,
                        content: basemapGallery.domNode,
                        expandIconClass: 'esri-icon-basemap',
                        label: 'Basemap',
                        expandTooltip: "Basemap Selector",
                    });

                    bgExpand.watch('expanded', function(newValue, oldValue, property, object) {
                        if (newValue) {
                            for (let i = 0; i < view.ui._components.length; i++) {
                                const c = view.ui._components[i];
                                if (c.widget) {
                                    if (["legend", "layers"].includes(c.widget.label.toLowerCase())) {
                                        c.widget.collapse();
                                    }
                                }
                            }
                        }
                    });

                    view.ui.add(bgExpand, {
                        position: "top-right",
                        index: 0
                    });
                }

                // *************************************************
                // LEGEND
                // *************************************************
                if (legendVisible) {
                    const legend = new Legend({
                        view: view,
                        container: document.createElement("div"),
                        layerInfos: []
                    });

                    let lgExpand = new Expand({
                        view: view,
                        content: legend.domNode,
                        expandIconClass: "esri-icon-maps",
                        label: "Legend",
                        expandTooltip: "Legend"
                    });

                    lgExpand.watch('expanded', function(newValue, oldValue, property, object) {
                        if (newValue) {
                            for (let i = 0; i < view.ui._components.length; i++) {
                                const c = view.ui._components[i];
                                if (c.widget) {
                                    if (["basemap", "layers"].includes(c.widget.label.toLowerCase())) {
                                        c.widget.collapse();
                                    }
                                }
                            }
                        }
                    });

                    view.ui.add(lgExpand, {
                        position: "top-right",
                        index: 2
                    });
                }

                // *************************************************
                // Layers
                // *************************************************
                if (layersVisible) {
                    // Create a LayerList widget instance and set
                    // its container to a div element
                    let layerList = new LayerList({
                        view: view,
                        container: document.createElement('div'),
                    });

                    // Create an Expand instance and set the content
                    // property to the DOM node of the basemap gallery widget
                    // Use an Esri icon font to represent the content inside
                    // of the Expand widget
                    const lyExpand = new Expand({
                        view: view,
                        content: layerList.domNode,
                        expandIconClass: 'esri-icon-layers',
                        label: 'Layers',
                        expandTooltip: "Layers",
                    });

                    lyExpand.watch('expanded', function(newValue, oldValue, property, object) {
                        if (newValue) {
                            for (let i = 0; i < view.ui._components.length; i++) {
                                const c = view.ui._components[i];
                                if (c.widget) {
                                    if (["basemap", "legend"].includes(c.widget.label.toLowerCase())) {
                                        c.widget.collapse();
                                    }
                                }
                            }
                        }
                    });

                    view.ui.add(lyExpand, {
                        position: "top-right",
                        index: 1
                    });
                }

                webMap
                    .load()
                    .then(function () {
                        // load the basemap to get its layers created
                        return webMap.basemap.load();
                    })
                    .then(function () {
                        // grab all the layers and load them
                        var allLayers = webMap.allLayers;
                        var promises = allLayers.map(function (layer) {
                            return layer.load();
                        });
                        return Promise.all(promises.toArray());
                    })
                    .then(function (layers) {
                        // each layer load promise resolves with the layer
                        // console.log('all ' + layers.length + ' layers loaded');
                        view.popup.dockOptions.position  = "bottom-center";

                        // Add this action to the popup so it is always available in this view
                        var detailsAction = {
                            title: "Details",
                            id: "navigate-details",
                            className: "esri-icon-description"
                        };

                        var template = {
                            // autocasts as new PopupTemplate()
                            title: "{subtitle}: {title}",
                            content: "{description}",
                            actions: [detailsAction]
                        };

                        // The function to execute when the navigate-details action is clicked
                        function navigateDetails(event) {
                            // console.log(event);
                            // console.log(view.popup.selectedFeature);
                            // TODO This needs configured
                            if (view.popup?.selectedFeature?.attributes?.Subtitle) {
                                dispatch(push('/details/summary?id={0}'.format(view.popup.selectedFeature.attributes.Subtitle)));
                            }
                        }

                        // This event fires for each click on any action
                        // Notice this event is handled on the default popup of the View
                        // NOT on an instance of PopupTemplate
                        view.popup.on("trigger-action", function(event){
                            // If the navigate-details action is clicked, fire the navigateDetails() function
                            if(event.action.id === "navigate-details"){
                                navigateDetails(event);
                            }
                        });

                        // create empty FeatureLayer for Project lat\lon
                        const projectsLayer = new FeatureLayer({
                            // create an instance of esri/layers/support/Field for each field object
                            title: "Projects",
                            fields: [
                                {
                                    name: "ObjectID",
                                    alias: "ObjectID",
                                    type: "oid"
                                },
                                {
                                    name: "Title",
                                    alias: "Title",
                                    type: "string"
                                },
                                {
                                    name: "Subtitle",
                                    alias: "Subtitle",
                                    type: "string"
                                },
                                {
                                    name: "Description",
                                    alias: "Description",
                                    type: "string"
                                }
                            ],
                            objectIdField: "ObjectID",
                            geometryType: "point",
                            spatialReference: { wkid: 4326 },
                            source: [], // adding an empty feature collection
                            renderer: {
                                type: "simple",
                                symbol: {
                                    type: "web-style", // autocasts as new WebStyleSymbol()
                                    styleName: "Esri2DPointSymbolsStyle",
                                    name: pointStyleName
                                }
                            },
                            popupTemplate: template
                        });
                        webMap.add(projectsLayer);

                        setProjectFeatureLayer(projectsLayer);

                    })
                    .then(function () {

                        if (disableGestures) {

                            view.on("key-down", function (event) {
                                var prohibitedKeys = ["+", "-", "Shift", "_", "="];
                                var keyPressed = event.key;
                                if (prohibitedKeys.indexOf(keyPressed) !== -1) {
                                    event.stopPropagation();
                                }
                            });

                            view.on("mouse-wheel", function (event) {
                                event.stopPropagation();
                            });

                            view.on("double-click", function (event) {
                                event.stopPropagation();
                            });

                            view.on("double-click", ["Control"], function (event) {
                                event.stopPropagation();
                            });

                            view.on("drag", function (event) {
                                event.stopPropagation();
                            });

                            view.on("drag", ["Shift"], function (event) {
                                event.stopPropagation();
                            });

                            view.on("drag", ["Shift", "Control"], function (event) {
                                event.stopPropagation();
                            });
                        }

                        // Keep references for later use
                        setWebMap(webMap);
                        setMapView(view);

                        if (useThemeBasemap) {
                            if (isThemeLight) {
                                webMap.basemap = config.data.lightTheme.basemap;
                            } else {
                                webMap.basemap = config.data.darkTheme.basemap;
                            }
                        }

                        // Display Map
                        setMapVisible(true);

                        // No longer loading
                        setMapLoading(false);

                    })
                    .catch(function (error) {
                        console.error('** ERROR ** -> {0}'.format(error), error);
                    });
            }
        );

        return function cleanup() {
        };

    }, []);

    // Map Loaded
    useEffect(() => {
        if (!mapLoading) {
            if (graphics.length === 0 && dataSource.data && dataSource.data.length > 0) {
                addData(dataSource)
            }
        }
    }, [mapLoading]);

    // Data Changed
    useEffect(() => {
        addData(dataSource);
    }, [dataSource.data]);

    // Theme Changed
    useEffect(() => {
        if (webMap) {
            if (useThemeBasemap) {
                if (isThemeLight) {
                    webMap.basemap = config.data.lightTheme.basemap;
                } else {
                    webMap.basemap = config.data.darkTheme.basemap;
                }
            }
        }

        if (isThemeLight) {
            const existingStyle = document.getElementById('esriDarkTheme');
            if (existingStyle) existingStyle.remove();
        } else {
            const css = `.sassy-theme .esri-widget,
      .sassy-theme .esri-widget--button,
      .sassy-theme .esri-menu,
      .sassy-theme .esri-popup__main-container,
      .sassy-theme .esri-popup .esri-popup__pointer-direction,
      .sassy-theme .esri-popup .esri-popup__button,
      .sassy-theme .esri-button,
      .sassy-theme .esri-input,
      .sassy-theme .esri-widget a {
        background-color: #424242;
        color: #fff;
      }

      .sassy-theme .esri-widget--button:focus,
      .sassy-theme .esri-widget--button:hover,
      .sassy-theme .esri-menu li:focus,
      .sassy-theme .esri-menu li:hover {
        background-color: #5b5b5b;
        color: #fff;
      }

      .sassy-theme .esri-button:focus,
      .sassy-theme .esri-button:hover {
        color: #fff;
      }

      .sassy-theme .esri-search__input::-moz-placeholder {
        color: #ccc;
        opacity: 1;
      }
      .sassy-theme .esri-search__input:-ms-input-placeholder {
        color: #ccc;
      }
      .sassy-theme .esri-search__input::-webkit-input-placeholder {
        color: #ccc;
      }`,
                head = document.head || document.getElementsByTagName('head')[0],
                style = document.createElement('style');
            style.setAttribute("id", "esriDarkTheme");
            style.type = 'text/css';
            head.appendChild(style);
            style.appendChild(document.createTextNode(css));
        }

    }, [isThemeLight]);

    function addData(dataSource) {
        if (dataSource.data) {
            loadModules(
                [
                    "esri/Map",
                    "esri/views/MapView",
                    "esri/Basemap",
                    "esri/layers/FeatureLayer",
                    "esri/Graphic",
                    "esri/geometry/Point"
                ],
                JSON.parse(esriOptions())
            ).then(
                ([
                     Map,
                     MapView,
                     Basemap,
                     FeatureLayer,
                     Graphic,
                     Point
                 ]) => {

                    // If the map is initialized and the layer
                    // is ready then add the project graphic
                    if (projectFeatureLayer) {

                        // Query for the features you want to remove
                        projectFeatureLayer.queryFeatures().then(function (results) {
                            // Edits object tells apply edits that you want to delete the features
                            const deleteEdits = {
                                deleteFeatures: results.features
                            };
                            // console.log(deleteEdits);

                            // Apply edits to the layer
                            projectFeatureLayer
                                .applyEdits(deleteEdits)
                                .then(function(results) {
                                    const newGraphics = [];
                                    for (let d of dataSource.data) {

                                        // Add new location(s)
                                        // NOTE: HC
                                        // console.log(d);

                                        if ((d.latitude) && (d.longitude)) {
                                            const graphic = new Graphic({
                                                geometry: {
                                                    type: "point",
                                                    latitude: d.latitude,
                                                    longitude: d.longitude
                                                },
                                                attributes: {
                                                    title: d.title,
                                                    subtitle: d.subtitle,
                                                    description: d.description,
                                                    gid: d.gid
                                                }
                                            });

                                            newGraphics.push(graphic);
                                        }
                                    }

                                    // addEdits object tells applyEdits that you want to add the features
                                    const addEdits = {
                                        addFeatures: newGraphics
                                    };
                                    // console.log(addEdits);

                                    // Save
                                    projectFeatureLayer
                                        .applyEdits(addEdits)
                                        .then(function(results) {
                                            if (newGraphics.length > 1) {
                                                // console.log('SmartMap: zooming to feature extent...')
                                                projectFeatureLayer.queryExtent().then(function(response) {
                                                    const extent = response.extent;
                                                    extent.expand(1.25);
                                                    // console.log('SmartMap: zooming to extent...', console.log(response.extent));
                                                    mapView.goTo(extent);
                                                });
                                            } else if (newGraphics.length === 1) {
                                                // console.log('SmartMap: zooming to pt at scale...');

                                                // Get location for single graphic
                                                const g = newGraphics[0];
                                                var pt = new Point({
                                                    latitude: g.geometry.y,
                                                    longitude: g.geometry.x
                                                });

                                                // Center and scale
                                                const options = { target: pt };
                                                if (panAndZoom) {
                                                    options.zoom = zoomLevel ? zoomLevel : 15
                                                }
                                                mapView.goTo(options);
                                            }

                                            projectFeatureLayer.refresh();
                                        })
                                        .catch(function(error) {
                                            console.log('**ERROR**', error);
                                        });

                                    // Persist graphics - potential reuse... if not purge
                                    setGraphics(newGraphics);

                                }).catch(function(error) {
                                console.log('**ERROR**', error);
                            });
                        });
                    }
                })
        }
    }

    return (
        <Root className={classes.root}>
            {showProgress && mapLoading && <LinearProgress className={classes.linearProgress}/>}
            <div
                id="mapMask"
                className={mapVisible ? classes.mapMask : classes.mapMaskHidden}
            >
            </div>
            <div id="viewDiv" className={[classes.mapDiv, 'sassy-theme'].join(' ')} ref={elementRef}></div>
        </Root>
    );
});
